import ReviewHandlingSession from '@app/model/review/ReviewHandlingSession';
import React from 'react';
import Utilities from '@util/Utilities';
import ReviewHandlingSessionLogEntryType from '@app/model/review/ReviewHandlingSessionLogEntryType';
import { ChatBubbleBottomCenterIcon } from '@heroicons/react/24/solid';
import useSecurity from '@util/SecurityHook';
import ReviewHandlingSessionLogEntry from '@app/model/review/ReviewHandlingSessionLogEntry';
import { XMarkIcon } from '@heroicons/react/24/outline';

const ReviewLogEntriesFragment: React.FunctionComponent<{
	isOpen: boolean,
	onClose: () => void,
	session: ReviewHandlingSession,
	onAddInternalNote: () => void
	onEditInternalNote: (logEntry: ReviewHandlingSessionLogEntry) => any
	onRemoveInternalNote: (logEntry: ReviewHandlingSessionLogEntry) => any
}> = ({ isOpen, onClose, session, onAddInternalNote, onEditInternalNote, onRemoveInternalNote }) => {

	const security = useSecurity();

	return isOpen ? <div className="space-y-6 p-4 lg:p-6 border-t border-gray-200">
		<div className="bg-white px-4 py-6 shadow sm:rounded-lg sm:px-6">
			<div className="flex items-baseline justify-between mb-5">
				<h3 className="text-base font-medium text-gray-900">Internal notes</h3>
				<button type="button" onClick={onClose}
								className="rounded-md text-gray-400 bg-gray-100 p-1 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2">
					<XMarkIcon className="w-4 h-4" />
				</button>
			</div>
			<ul role="list" className="space-y-6">
				{session.logEntries.map((logEntry, index) => (
					<li key={logEntry.id} className="relative flex gap-x-4">
						<div
							className={Utilities.classNames(
								index === session.logEntries.length - 1 ? 'h-6' : '-bottom-6',
								'absolute left-0 top-0 flex w-6 justify-center',
							)}
						>
							<div className="w-px bg-gray-200" />
						</div>
						{logEntry.type === ReviewHandlingSessionLogEntryType.VIEWED && <>
							<div className="relative flex w-6 h-6 flex-none items-center justify-center bg-white">
								<div className="w-1.5 h-1.5 rounded-full bg-gray-100 ring ring-gray-300" />
							</div>
							<p className="flex-auto py-0.5 text-xs/5 text-gray-500">
								<span className="font-medium text-gray-900">{logEntry.profileName}</span> viewed the review.
							</p>
							<time dateTime={logEntry.creationDate} className="flex-none py-0.5 text-xs/5 text-gray-500">
								{Utilities.timeDifference(logEntry.creationDate)} ago
							</time>
						</>}
						{logEntry.type === ReviewHandlingSessionLogEntryType.NOTES && <>
							<div className="relative mt-3 flex w-6 h-6 flex-none items-center justify-center bg-white">
								<ChatBubbleBottomCenterIcon className="w-4 h-4 text-gray-300" />
							</div>
							<div className="flex-auto rounded-md p-3 ring-1 ring-inset ring-gray-200">
								<div className="flex justify-between gap-x-4">
									<div className="py-0.5 text-xs/5 text-gray-500">
										<span className="font-medium text-gray-900">{logEntry.profileName}</span> noted
									</div>
									<time dateTime={logEntry.creationDate} className="flex-none py-0.5 text-xs/5 text-gray-500">
										{Utilities.timeDifference(logEntry.creationDate)} ago
									</time>
								</div>
								<p className="text-sm/6 text-gray-500">{logEntry.notes ?? '-'}</p>
								{security.principal!.id === logEntry.profileId &&
									<div className="flex justify-end items-center gap-x-2 text-right">
										<a onClick={() => onEditInternalNote(logEntry)}
											 className="text-xs text-blue-500 hover:text-blue-600 cursor-pointer">Edit</a>
										<span className="text-xs text-gray-400">|</span>
										<a onClick={() => onRemoveInternalNote(logEntry)}
											 className="text-xs text-red-500 hover:text-red-600 cursor-pointer">Delete</a>
									</div>}
							</div>
						</>}
					</li>
				))}
			</ul>
			<div className="mt-6 pt-4 border-t border-gray-200">
				<button className="btn btn-white text-blue-500" onClick={onAddInternalNote}>Add Internal Note</button>
			</div>
		</div>
	</div> : null;
};

export default ReviewLogEntriesFragment;